/**
 * <price-range> — native dual-thumb price facet for the Hippo collection.
 *
 * Light-DOM custom element wrapping the markup in snippets/hippo-price-range.liquid.
 * A pixel-perfect port of the Claude Design mockup: two overlaid native
 * <input type="range"> thumbs drive a gold fill bar and two editable currency
 * fields (typed values are two-way bound back to the slider), and mirror their
 * values into two hidden read-only inputs that carry the real
 * filter.*.param_name query params. Filters are applied by submitting the
 * surrounding server-driven GET form (real URL, no AJAX): in sidebar mode
 * (data-auto-apply="true") releasing a thumb submits the form; in the drawer the
 * "Show results" button submits (the hidden inputs already hold the values).
 *
 * No vendor dependency — listeners are wired in connectedCallback and torn down in
 * disconnectedCallback, so the behavior re-attaches whenever the element is
 * (re-)inserted, surviving `shopify theme dev` hot-reloads and Section-Rendering
 * swaps.
 */
class PriceRange extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLInputElement | null} */
    this.lo = this.querySelector('.range-min')
    /** @type {HTMLInputElement | null} */
    this.hi = this.querySelector('.range-max')
    /** @type {HTMLElement | null} */
    this.fill = this.querySelector('.range-track__fill')
    /** @type {HTMLInputElement | null} */
    this.fieldMin = this.querySelector('.price-range__field-min')
    /** @type {HTMLInputElement | null} */
    this.fieldMax = this.querySelector('.price-range__field-max')
    /** @type {HTMLInputElement | null} */
    this.inputMin = this.querySelector('.price-range__input-min')
    /** @type {HTMLInputElement | null} */
    this.inputMax = this.querySelector('.price-range__input-max')

    if (!this.lo || !this.hi) return

    this.autoApply = this.dataset.autoApply === 'true'
    this.usesComma = this.dataset.usesComma === 'true'
    this.minRange = parseFloat(this.dataset.min || '') || 0
    this.maxRange = parseFloat(this.dataset.max || '') || 100
    // Currency-agnostic granularity: ~200 stops across the range (min 1 unit).
    this.step = Math.max(1, Math.round((this.maxRange - this.minRange) / 200))

    // Native range inputs need a step for keyboard/drag granularity.
    this.lo.step = String(this.step)
    this.hi.step = String(this.step)

    this.onInput = this.onInput.bind(this)
    this.onChange = this.onChange.bind(this)
    this.lo.addEventListener('input', this.onInput)
    this.hi.addEventListener('input', this.onInput)
    this.lo.addEventListener('change', this.onChange)
    this.hi.addEventListener('change', this.onChange)

    if (this.fieldMin && this.fieldMax) {
      this.onFieldInput = this.onFieldInput.bind(this)
      this.onFieldCommit = this.onFieldCommit.bind(this)
      this.onFieldKeydown = this.onFieldKeydown.bind(this)
      this.onFieldFocus = this.onFieldFocus.bind(this)
      ;[this.fieldMin, this.fieldMax].forEach((f) => {
        f.addEventListener('input', this.onFieldInput)
        f.addEventListener('change', this.onFieldCommit) // fires on blur
        f.addEventListener('keydown', this.onFieldKeydown)
        f.addEventListener('focus', this.onFieldFocus)
      })
    }

    this.syncUI()
  }

  disconnectedCallback() {
    if (this.lo) {
      this.lo.removeEventListener('input', this.onInput)
      this.lo.removeEventListener('change', this.onChange)
    }
    if (this.hi) {
      this.hi.removeEventListener('input', this.onInput)
      this.hi.removeEventListener('change', this.onChange)
    }
    ;[this.fieldMin, this.fieldMax].forEach((f) => {
      if (!f) return
      f.removeEventListener('input', this.onFieldInput)
      f.removeEventListener('change', this.onFieldCommit)
      f.removeEventListener('keydown', this.onFieldKeydown)
      f.removeEventListener('focus', this.onFieldFocus)
    })
  }

  /**
   * Reflect the current thumb values into the fill bar and the editable fields.
   * The field the user is typing in is skipped so live input isn't clobbered.
   */
  syncUI() {
    if (!this.lo || !this.hi) return
    const a = parseFloat(this.lo.value)
    const b = parseFloat(this.hi.value)
    const span = this.maxRange - this.minRange || 1

    if (this.fill) {
      this.fill.style.left = ((a - this.minRange) / span) * 100 + '%'
      this.fill.style.right = 100 - ((b - this.minRange) / span) * 100 + '%'
    }
    if (this.fieldMin && document.activeElement !== this.fieldMin) {
      this.fieldMin.value = this.formatField(a)
    }
    if (this.fieldMax && document.activeElement !== this.fieldMax) {
      this.fieldMax.value = this.formatField(b)
    }
  }

  /**
   * Live drag: keep the thumbs at least one step apart (no crossover), then
   * refresh the fill + labels.
   * @param {Event} e
   */
  onInput(e) {
    if (!this.lo || !this.hi) return
    let a = parseInt(this.lo.value, 10)
    let b = parseInt(this.hi.value, 10)
    if (a > b - this.step) {
      if (e.target === this.lo) a = b - this.step
      else b = a + this.step
      this.lo.value = String(a)
      this.hi.value = String(b)
    }
    this.syncUI()
  }

  /**
   * Thumb released / committed: write the real param inputs and, in sidebar
   * mode, submit the surrounding GET form.
   */
  onChange() {
    if (!this.lo || !this.hi) return
    this.writeParams()

    if (this.autoApply) {
      const form = this.closest('form')
      if (form) form.submit()
    }
  }

  /**
   * Mirror the slider values into the hidden params that actually filter.
   */
  writeParams() {
    if (this.inputMin && this.lo) this.inputMin.value = this.lo.value
    if (this.inputMax && this.hi) this.inputMax.value = this.hi.value
  }

  /**
   * Strip symbol + grouping, normalize the decimal separator → float in slider
   * units. Honors the comma-decimal flag so it round-trips in any currency.
   * @param {string | number | null} str
   * @returns {number}
   */
  parseNum(str) {
    if (str == null) return NaN
    const grp = this.usesComma ? '.' : ','
    const dec = this.usesComma ? ',' : '.'
    let s = String(str).replace(new RegExp('[^0-9\\' + grp + '\\' + dec + ']', 'g'), '')
    s = s.split(grp).join('').replace(dec, '.')
    return parseFloat(s)
  }

  /**
   * Float → grouped display string (no currency symbol; the symbol lives in the
   * separate .price-field__prefix span).
   * @param {number | string} value
   * @returns {string}
   */
  formatField(value) {
    const n = Number(value)
    if (!isFinite(n)) return ''
    const grp = this.usesComma ? '.' : ','
    const dec = this.usesComma ? ',' : '.'
    const fixed = Number.isInteger(n) ? String(n) : n.toFixed(2)
    const [intPart, frac] = fixed.split('.')
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, grp)
    return frac ? grouped + dec + frac : grouped
  }

  /**
   * Clamp a value to the facet's range bounds.
   * @param {number} n
   * @returns {number}
   */
  clampVal(n) {
    return Math.min(Math.max(n, this.minRange), this.maxRange)
  }

  /**
   * Select-all on focus so a tap-and-type replaces the value cleanly.
   * @param {Event} e
   */
  onFieldFocus(e) {
    const field = /** @type {HTMLInputElement} */ (e.target)
    field.select()
  }

  /**
   * Live typing: move the slider + fill, leave the focused field text alone, no
   * submit yet.
   * @param {Event} e
   */
  onFieldInput(e) {
    if (!this.lo || !this.hi) return
    const which = e.target === this.fieldMin ? 'min' : 'max'
    let a =
      which === 'min' && this.fieldMin
        ? this.clampVal(this.parseNum(this.fieldMin.value))
        : parseFloat(this.lo.value)
    let b =
      which === 'max' && this.fieldMax
        ? this.clampVal(this.parseNum(this.fieldMax.value))
        : parseFloat(this.hi.value)
    if (isNaN(a)) a = this.minRange
    if (isNaN(b)) b = this.maxRange
    if (which === 'min' && a > b) b = a
    if (which === 'max' && b < a) a = b
    this.lo.value = String(a)
    this.hi.value = String(b)
    this.syncUI() // skips the focused field
  }

  /**
   * Commit (blur / Enter): clamp, enforce the min gap, swap if reversed,
   * reformat, write params, and in sidebar mode submit the form.
   */
  onFieldCommit() {
    if (!this.lo || !this.hi || !this.fieldMin || !this.fieldMax) return
    let a = this.clampVal(this.parseNum(this.fieldMin.value))
    let b = this.clampVal(this.parseNum(this.fieldMax.value))
    if (isNaN(a)) a = this.minRange
    if (isNaN(b)) b = this.maxRange
    if (a > b) {
      const t = a
      a = b
      b = t
    }
    if (b - a < this.step) b = Math.min(this.maxRange, a + this.step)
    this.lo.value = String(a)
    this.hi.value = String(b)
    this.fieldMin.value = this.formatField(a) // force-format both, even if still focused
    this.fieldMax.value = this.formatField(b)
    this.syncUI()
    this.writeParams()
    if (this.autoApply) {
      const form = this.closest('form')
      if (form) form.submit()
    }
  }

  /**
   * Enter commits the field (blur fires change → commit).
   * @param {KeyboardEvent} e
   */
  onFieldKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      const field = /** @type {HTMLInputElement} */ (e.target)
      field.blur()
    }
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange)
}
